import type { Worker } from "tesseract.js";

/**
 * Moteur OCR Tesseract.js (WASM, 100 % navigateur) — langues fra+eng.
 *
 * Le worker est créé paresseusement au premier usage et réutilisé
 * (les modèles de langue ~11 Mo sont mis en cache par le navigateur
 * après le premier téléchargement). Sortie v6 : texte + blocks
 * (bounding boxes de lignes) requis pour la reconstruction du layout.
 */

/** Ligne OCR positionnée — le contrat attendu par le moteur heuristique serveur. */
export type OcrLine = {
  text: string;
  x: number;
  top: number;
  width: number;
  height: number;
  bold: boolean;
};

let workerPromise: Promise<Worker> | null = null;
let preloadPromise: Promise<void> | null = null;
let progressHandler: ((progress: number) => void) | null = null;

const getWorker = async (): Promise<Worker> => {
  if (!workerPromise) {
    workerPromise = (async () => {
      const { createWorker, PSM } = await import("tesseract.js");
      const worker = await createWorker("fra+eng", 1, {
        logger: (message: { status?: string; progress?: number }) => {
          if (message.status === "recognizing text" && progressHandler) {
            progressHandler(message.progress ?? 0);
          }
        },
      });
      // Analyse de page automatique : le mode par défaut (bloc unique) ignore
      // les grands titres, dont le nom du candidat, et mélange les colonnes.
      await worker.setParameters({ tessedit_pageseg_mode: PSM.AUTO });
      return worker;
    })();
  }
  return workerPromise;
};

/**
 * Reconnaît un canvas et retourne les lignes positionnées (bbox par ligne).
 * Tesseract ne donne pas la graisse : `bold` reste faux, le serveur s'appuie
 * sur les hauteurs relatives, les majuscules et la structure des lignes.
 */
export const ocrCanvasLines = async (
  canvas: HTMLCanvasElement,
  onProgress?: (progress: number) => void,
): Promise<OcrLine[]> => {
  progressHandler = onProgress ?? null;
  try {
    const worker = await getWorker();
    const { data } = await worker.recognize(canvas, {}, { blocks: true });
    const lines: OcrLine[] = [];

    for (const block of data.blocks ?? []) {
      for (const paragraph of block.paragraphs ?? []) {
        for (const line of paragraph.lines ?? []) {
          const text = (line.text ?? line.words?.map((word) => word.text).join(" ") ?? "").trim();
          if (!text) continue;
          const { x0, y0, x1, y1 } = line.bbox;
          const height = Math.max(1, y1 - y0);
          lines.push({
            text,
            x: x0,
            top: y0,
            width: Math.max(1, x1 - x0),
            height,
            bold: false,
          });
        }
      }
    }
    return lines;
  } finally {
    progressHandler = null;
  }
};

/** Libère le worker (à appeler à la fermeture du dialog). */
export const terminateOcrWorker = async (): Promise<void> => {
  if (!workerPromise) return;
  const worker = await workerPromise;
  await worker.terminate();
  workerPromise = null;
};

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  PRÉCHARGEMENT DU MOTEUR OCR (~20 Mo : worker 0,1 + core ~3 + fra 6,0 + eng 10,4)
 * ─────────────────────────────────────────────────────────────────────────────
 *  Télécharge worker + core WASM + modèles fra/eng en arrière-plan, AVANT
 *  le premier vrai usage : la reconnaissance démarre alors instantanément.
 *
 *  - Stockage : IndexedDB (cacheMethod "write") → PERSISTANT entre les
 *    sessions : le coût de ~20 Mo n'est payé qu'UNE fois par navigateur.
 *  - Idempotent (une fois par session) ;
 *  - Respecte Data Saver / connexions mesurées (pas de préchargement) ;
 *  - Best-effort : un échec est silencieux, l'usage réel retéléchargera.
 *
 *  À déclencher à un moment pertinent : survol de l'action d'import
 *  (implémenté dans resume-create-card), ou au boot de la plateforme
 *  si la direction préfère maximiser la fluidité (voir
 *  docs/markdown/ocr-import-deploiement.md).
 */
export const preloadOcrEngine = (): Promise<void> => {
  if (preloadPromise) return preloadPromise;
  preloadPromise = (async () => {
    const connection = (navigator as Navigator & { connection?: { saveData?: boolean } })
      .connection;
    if (connection?.saveData) return;
    try {
      const worker = await getWorker();
      const canvas = document.createElement("canvas");
      canvas.width = 1;
      canvas.height = 1;
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, 1, 1);
      }
      // Image blanche 1×1 : déclenche le téléchargement complet (worker +
      // core + modèles) et leur mise en cache, sans travail d'OCR notable.
      await worker.recognize(canvas, {}, { text: true });
    } catch {
      workerPromise = null; // permettra un nouvel essai au prochain appel
    }
  })();
  return preloadPromise;
};
